import { cn } from '@glade-ai/ui';
import React, { type ChangeEvent, type ForwardRefRenderFunction, forwardRef, useCallback, useRef } from 'react';

import Label from '../shared/Label';

import s from './Input.module.scss';

export type InputProps = {
  id?: string;
  name?: string;
  label?: string;
  compact?: boolean;
  disabled?: boolean;
  description?: string;
  value?: string | null;
  placeholder?: string;
  error?: string;
  hasError?: boolean;
  optional?: boolean;
  hint?: string;
  suffix?: string;
  prefix?: React.ReactNode;
  type?: 'number' | 'text' | 'tel' | 'password';
  min?: number;
  inputMode?: 'text' | 'email' | 'search' | 'tel' | 'url' | 'none' | 'numeric' | 'decimal';
  onChange?: (value: string, event: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  onInput?: (value: string, event: ChangeEvent<HTMLInputElement>) => void;
  isFetching?: boolean;
  className?: string;
  testId?: string;
};

const Input: ForwardRefRenderFunction<HTMLInputElement, InputProps> = (
  {
    id,
    name,
    label,
    compact,
    disabled,
    description,
    value,
    placeholder,
    error,
    hasError,
    optional,
    hint,
    suffix,
    prefix,
    type = 'text',
    min,
    inputMode,
    onChange,
    onBlur,
    onFocus,
    onInput,
    isFetching,
    className,
    testId,
  },
  ref,
) => {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onInputRef = useRef(onInput);
  onInputRef.current = onInput;

  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>): void => {
    onChangeRef.current?.(event.target.value, event);
  }, []);

  const handleInput = useCallback((event: ChangeEvent<HTMLInputElement>): void => {
    onInputRef.current?.(event.target.value, event);
  }, []);

  return (
    <Label
      label={label || ''}
      hideLabel={!label && !description}
      required={!optional}
      description={description}
      errorMessage={error}
      hint={hint}
      compact={compact}
      isFetching={isFetching}
    >
      <div className={s['input-field-wrapper']}>
        {prefix ? <span className={s.prefix}>{prefix}</span> : null}
        <input
          id={id}
          ref={ref}
          name={name}
          type={type}
          value={value ?? ''}
          placeholder={placeholder}
          disabled={disabled}
          required={!optional}
          onChange={handleChange}
          onInput={handleInput}
          onBlur={onBlur}
          onFocus={onFocus}
          min={min}
          aria-invalid={hasError || !!error || undefined}
          className={cn(
            s['input-field'],
            'focus-ring',
            (hasError || !!error) && s.error,
            !!suffix && s['has-suffix'],
            !!prefix && s['has-prefix'],
            className,
          )}
          inputMode={inputMode}
          data-testid={testId}
        />
        {suffix ? <span className={s.suffix}>{suffix}</span> : null}
      </div>
    </Label>
  );
};

export default forwardRef(Input);
